<script lang="ts">
  /**
   * Reaction buttons plus the strip the bursts animate in.
   *
   * The strip is a reserved, fixed-height band *next to* the player rather
   * than an overlay on top of it: bursts must not cover captions, controls, or
   * the buffering/error overlays (plan/04 social item 5), and a reserved band
   * also means a burst never reflows the page.
   *
   * Reduced motion is handled in CSS: the same burst appears and holds
   * statically for its lifetime instead of rising and fading. The event is
   * never suppressed, only its movement.
   */
  import type { RoomState } from '../room/state.svelte';
  import { REACTION_CHOICES } from './reactions';

  const { room }: { room: RoomState } = $props();

  const bursts = $derived(room.social.bursts);
  const disabled = $derived(!room.live || room.social.reactionCoolingDown);
</script>

<section class="reactions bare" aria-labelledby="reactions-heading">
  <h2 id="reactions-heading" class="visually-hidden">Reactions</h2>

  <div class="buttons">
    {#each REACTION_CHOICES as choice (choice.kind)}
      <button
        type="button"
        {disabled}
        aria-label="React with {choice.label.toLowerCase()}"
        title={room.live ? choice.label : 'Not connected to the room right now.'}
        onclick={() => void room.social.sendReaction(choice.kind)}
      >
        <span class="glyph" aria-hidden="true">{choice.glyph}</span>
        <span class="label">{choice.label}</span>
      </button>
    {/each}
  </div>

  <!-- Announced politely: a reaction is ambient, never an interruption. -->
  <div class="stream" role="status" aria-live="polite" aria-relevant="additions">
    {#each bursts as burst (burst.seq)}
      <span class="burst" data-lane={burst.lane}>
        <span class="glyph" aria-hidden="true">{burst.glyph}</span>
        <span class="visually-hidden">{burst.username} reacted: {burst.label}</span>
      </span>
    {/each}
  </div>
</section>

<style>
  .reactions {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
    min-width: 0;
  }

  .buttons {
    display: flex;
    gap: 0.35rem;
    flex-wrap: wrap;
  }

  .buttons button {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.85rem;
  }

  .glyph {
    font-size: 1.05rem;
    line-height: 1;
  }

  /* The text label is the accessible name's visible twin; it collapses away on
     narrow viewports where the glyph plus `aria-label` still carry it. */
  @media (width <= 34rem) {
    .buttons .label {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
    }
  }

  /* Reserved band: bounded height, no overlap with the player above it. */
  .stream {
    position: relative;
    flex: 1 1 8rem;
    min-width: 6rem;
    height: 2.6rem;
    overflow: hidden;
  }

  .burst {
    position: absolute;
    bottom: 0;
    font-size: 1.4rem;
    line-height: 1;
    animation: rise 3s ease-out forwards;
  }

  .burst[data-lane='0'] {
    left: 4%;
  }

  .burst[data-lane='1'] {
    left: 28%;
  }

  .burst[data-lane='2'] {
    left: 52%;
  }

  .burst[data-lane='3'] {
    left: 76%;
  }

  @keyframes rise {
    from {
      transform: translateY(0.9rem) scale(0.8);
      opacity: 0;
    }

    15% {
      transform: translateY(0.4rem) scale(1);
      opacity: 1;
    }

    to {
      transform: translateY(-1.2rem) scale(1);
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .burst {
      /* Still visible for its full lifetime, just not moving or fading. */
      animation: none;
      bottom: 0.5rem;
      opacity: 1;
    }
  }
</style>
